import { hasValue, reader } from '@woonstad/helpers-utils';

export async function compressFiles(files: File[], maxWidth = 2000, quality = 0.75, fileType = 'image/jpeg'): Promise<File[]> {
  // Map over the files to get an array of promises
  const filePromises = files.map(async (file) => {
    const compressedFile = await compressFile(file, maxWidth, quality, fileType);

    if (hasValue(compressedFile)) {
      return [compressedFile];
    }

    return [];
  });

  // Await all promises and flatten the resulting arrays
  const compressedFiles = await Promise.all(filePromises);
  const flattenedFiles = compressedFiles.flat();

  // Filter out any undefined or null values
  return flattenedFiles.filter((file) => hasValue(file));
}

export async function compressFile(file: File, maxWidth = 2000, quality = 0.75, fileType = 'image/jpeg'): Promise<File | null> {
  // Read the file
  const base64File = await reader(file);

  return new Promise((resolve, reject) => {
    // Check the mime type of the received file to check if we need to abort... this only works for images
    if (!file.type.startsWith('image/')) {
      return resolve(null);
    }

    // Get the image dimensions
    const img = new Image();

    img.onload = () => {
      // Do everything we have to do with the image
      const {
        height,
        width,
      } = img;

      // Create the new size of the image
      const aspectRatio = height / width;
      const canvasWidth = width <= maxWidth ? width : maxWidth;
      const canvasHeight = canvasWidth * aspectRatio;

      // Create a new canvas
      const canvas = document.createElement('canvas');

      // Set the height, width of the canvas
      canvas.height = canvasHeight;
      canvas.width = canvasWidth;

      // Create the context for the canvas
      const ctx = canvas.getContext('2d');

      if (hasValue(ctx)) {
        // clear the canvas
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        // Draw the image on the canvas in the wanted size
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

        // Extract a new blob from the canvas
        canvas.toBlob((blob) => {
          // Create a new File from the base64 string
          if (hasValue(blob)) {
            resolve(new File([blob], file.name, {
              type: fileType,
            }));
          }
        }, fileType, quality);
      }
    };

    img.onerror = (error) => {
      console.error(error);
      reject(new Error('Image failed to load'));
    };

    img.src = base64File;
  });
}
